import React, { useState, useEffect } from 'react';
import { Job } from './JobTypes';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'services/settings/SettingsContext';
import { useAllowedRight } from 'services/permissions/permissionChecks';
import { useModal } from 'components/modals/ModalContext';
import JobModal from './JobModal';
import ProjectJobForm from '../projects/ProjectJobForm';
import { Project } from '../../views/projects/ProjectTypes';
import { DetailCardType } from 'types/DetailPageTypes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEuroSign } from '@fortawesome/free-solid-svg-icons';
import '../../style/scss/card.scss';
import '../../style/scss/tooltip.scss';

export interface ProjectJobsTabProps extends DetailCardType {
    project: Project;
}

const ProjectJobsTab: React.FC<ProjectJobsTabProps> = ({ 
    project, isEditable
}) => {
    const { t } = useTranslation();
    const { initializeModal } = useModal();
    const { userLocale } = useSettings();
    const [rows, setRows] = useState<Job[]>([]);
    const [userHasOnlyViewRights] = useState<boolean>(useAllowedRight('only_view'));
    
    // Set the current rows
    useEffect(() => {
        if (project.jobs && project.jobs.length > 0) {
            setRows(project.jobs);
        }
    }, [project.jobs]);
    
    // Handle create project job
    const handleCreateProjectJob = async () => {
        initializeModal(
            React.cloneElement(
                <ProjectJobForm 
                    linkedItem={{ 
                        project: project.id,
                        ...(project.client && { 
                            client: {
                                id: project.client.id,
                                name: project.client.name,
                            }
                        }),
                        ...(project.work_location && {
                            work_location: {
                                id: project.work_location.id,
                                address: project.work_location.address
                            }
                        }),
                        ...(project.location_person && {
                            location_person: {
                                id: project.location_person.id,
                                name: project.location_person.name
                            }
                        }),
                        plan_method: 'add_to_storage'
                    }}
                />
            ), {
                title: t('button.add_object_label', { object_name: t(`job.general.object_name.singular`) }),
                modalSize: 'add-form'
            } 
        );
    }

    // Handle open the job
    const handleOpenJob = (jobId: number | null) => {
        if (!jobId) return;
        initializeModal(
            React.cloneElement(<JobModal />), { 
                itemId: jobId,
                modalSize: 'large' 
            } 
        );
    }

    // Determine to show the worklocation column
    const showWorkLocationColumn = rows
        .filter(job => !job.deleted)
        .some(job => job.work_location?.id !== project.work_location?.id);

    // Determine the grid template columns, the last column holds the invoiced indicator
    const gridTemplate = showWorkLocationColumn ? '2fr 3fr 4fr 5fr 2.4fr 128px 1.5rem' : '2fr 3fr 9fr 2.4fr 128px 1.5rem';

    // Render the invoiced indicator of a job
    const renderInvoicedIndicator = (job: Job) => {
        if (!job.is_invoiced) return <div />;
        return (
            <div className='invoiced-indicator-container'>
                <span className='tooltip-icon'>
                    <span className='invoiced-indicator'>
                        <FontAwesomeIcon icon={faEuroSign} />
                    </span>
                    <span className='tooltip'>{t('project.general.job_invoiced_tooltipText')}</span>
                </span>
            </div>
        );
    };

    return (
        <div className='card project-jobs-card'>
            <div className='project-jobs-individual'>
                <div className="project-job-list">
                    <div 
                        className='list-header'
                        style={{ gridTemplateColumns: gridTemplate }}
                    >
                        <div>{t('project.general.job_number_label')}</div>
                        <div>{t('project.general.job_type_label')}</div>
                        {showWorkLocationColumn && (
                            <div>{t('project.general.work_location_label')}</div>
                        )}
                        <div>{t('project.general.job_description_label')}</div>
                        <div>{t('project.general.job_target_date_label')}</div>
                        <div>{t('project.general.job_status_label')}</div>
                        <div />
                    </div>
                    {rows?.filter(row => !row.deleted).map(job => {
                        return (
                            <div key={job.id}
                                 className='list-row'
                                 onClick={() => handleOpenJob(job.id)}
                                 style={{ gridTemplateColumns: gridTemplate }}
                            >
                                <p>{job.number}</p>
                                <p>{job.workflow.name}</p>
                                {showWorkLocationColumn && (
                                    <p>{job.work_location?.address}</p>
                                )}
                                <p>{job.main_description}</p>
                                <p>
                                    {job.target_date && (
                                        new Date(job.target_date).toLocaleDateString(userLocale, { year: 'numeric', month: '2-digit', day: '2-digit' })
                                    )}
                                </p>
                                <div className="badge-container">
                                    <div className={`status-badge ${job.status_condition}`}>
                                        {t(`workflow.default_status_names.${job.status_name}`, { defaultValue: job.status_name })}
                                    </div>
                                </div>
                                {renderInvoicedIndicator(job)}
                            </div>
                        );
                    })}
                </div>
                {isEditable && !userHasOnlyViewRights && (
                    <div onClick={handleCreateProjectJob}
                        className="add-new-button">
                        {t('project.general.add_new_job')}
                    </div>
                )}
            </div>
        </div>
    )
}

export default ProjectJobsTab;